import Vue from 'vue'
import VueRouter from 'vue-router'

import store from '@/store'

Vue.use(VueRouter)

// Map config shared by the routes that render the persistent main map (index
// + location). Unlike desktop — where GeoFleet.vue renders the map once,
// outside the router — mobile binds the map to each route's `map` named view,
// so every route that needs the map visible must supply this. Keeping it in
// one place stops the two route definitions from drifting apart.
const mainMapProps = () => ({
    map: 'silica',
    advanced: true,
    // The mobile map IS the main map: it must react to the `locationRequest`
    // bus (center + marker) and drive the `/location` URL from its search bar,
    // exactly like the desktop main map. GeofleetMap gates all of that on
    // `isMainMap`, which defaults false — without this the location route
    // (and focus/route-drawing) silently no-op on mobile.
    isMainMap: true,
    // Sprint 19 (REFACTOR-030): getter `value(key)` col default da schema
    // (true) al posto della lettura raw — parità: assente → undefined
    // attivava comunque il prop-default true di GeofleetMap.
    cluster: store.getters['config/value']('activeVehicleClustering'),
    ptvToken: Vue.prototype.$env.ptv_password,
    poi: true,
    noAutoCenterMap: false,
    extraControls: {
        vehiclesMenu: true,
        mobileSettings: true,
        mobileLogout: true
    }
})

const routes = [
    /*
     * GEOFLEET
     */
    {
        path: '/',
        component: () =>
            import(/* webpackChunkName: "geofleet" */ '@/views/geofleet/GeoFleetMobile'),
        children: [
            {
                path: '/',
                name: 'geofleet.index',
                components: {
                    map: () => import(/* webpackChunkName: "geofleet" */ '@/components/GeofleetMap')
                },
                // Pass the props object to the component using the props option
                props: {
                    default: false,
                    // Use a function so we can read the config from the Vuex store
                    map: mainMapProps
                }
            },
            {
                path: '/vehicles',
                name: 'geofleet.vehicles',
                components: {
                    default: () =>
                        import(
                            /* webpackChunkName: "geofleet" */ '@/views/geofleet/components/MainPanel'
                        )
                }
            },
            {
                path: '/vehicle/:id',
                name: 'geofleet.vehicle_details',
                components: {
                    default: () =>
                        import(
                            /* webpackChunkName: "geofleet" */ '@/views/geofleet/components/DetailsPanel'
                        )
                }
            },
            {
                path: '/vehicle/:id/driver_details/:driver_id/plafond',
                name: 'geofleet.driver_plafond',
                components: {
                    default: () =>
                        import(
                            /* webpackChunkName: "geofleet" */ '@/views/geofleet/components/PlafondPage'
                        )
                }
            },
            {
                // Mobile shows route analysis inline in the details panel's
                // "map" tab (ActivityWrapper route view) instead of the desktop
                // AnalyzePage. Same DetailsPanel component as
                // `geofleet.vehicle_details` — the URL only selects the route
                // view — so toggling between them reuses the mounted instance.
                path: '/vehicle/:id/route',
                name: 'geofleet.vehicle_analyze',
                components: {
                    default: () =>
                        import(
                            /* webpackChunkName: "geofleet" */ '@/views/geofleet/components/DetailsPanel'
                        )
                }
            },
            {
                // Mirrors desktop `geofleet.poi_management`. PoiManagerPanel renders
                // and edits POI markers on the shared map; keep the map mounted.
                path: '/poi-management',
                name: 'geofleet.poi_management',
                components: {
                    map: () =>
                        import(/* webpackChunkName: "geofleet" */ '@/components/GeofleetMap'),
                    default: () =>
                        import(
                            /* webpackChunkName: "geofleet" */ '@/views/geofleet/components/PoiManagementPage'
                        )
                },
                props: {
                    map: mainMapProps
                }
            },
            {
                // Mirrors desktop `geofleet.settings`. SettingsPage is a self-
                // contained panel (like `vehicles`/`vehicle_details`), so it does
                // not mount the persistent map.
                path: '/settings',
                name: 'geofleet.settings',
                components: {
                    default: () =>
                        import(
                            /* webpackChunkName: "geofleet" */ '@/views/geofleet/components/SettingsPage'
                        )
                }
            },
            {
                // Mirrors the desktop `geofleet.location` route. The shared
                // GeofleetMap drives the URL here (syncLocationRoute) on both
                // platforms; mobile previously lacked the route, so that
                // navigation silently no-op'd. LocationPage renders null and
                // only feeds the `locationRequest` bus, so the map must stay
                // mounted — hence the `map` view alongside it.
                path: '/location',
                name: 'geofleet.location',
                components: {
                    map: () =>
                        import(/* webpackChunkName: "geofleet" */ '@/components/GeofleetMap'),
                    default: () =>
                        import(
                            /* webpackChunkName: "geofleet" */ '@/views/geofleet/components/LocationPage'
                        )
                },
                props: {
                    map: mainMapProps
                }
            }
        ]
    },

    /*
     * GEOFLEET SHARE LINK
     */
    {
        path: '/view-shared-link/:token',
        name: 'viewvehicle.index',
        component: () =>
            import(
                /* webpackChunkName: "sharedlinks" */ './../views/viewsharedlink/viewSharedLink.vue'
            )
    },

    /**
     * Errors/Service pages
     */
    {
        path: '/errors/',
        name: 'errors.index',
        component: () =>
            import(/* webpackChunkName: "errors" */ './../views/errors/ErrorLayout.vue'),
        children: [
            {
                name: 'error-403',
                path: 'permissions',
                component: () =>
                    import(
                        /* webpackChunkName: "errors" */ './../views/errors/ErrorPermissions.vue'
                    )
            }
        ]
    }
]

const router = new VueRouter({
    base: process.env.BASE_URL,
    routes
})

export default router
